import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Switch, Divider, InputLabel, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";

import { useCreateTaxZone } from "../../../hooks/tax/useCreateTaxZone";
import { useUpdateTaxZone } from "../../../hooks/tax/useUpdateTaxZone";

const FormAddEditTaxZone = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [isActive, setIsActive] = useState(true);

    const { createTaxZoneMutate, isCreating } = useCreateTaxZone();
    const { updateTaxZoneMutate, isUpdating } = useUpdateTaxZone();
    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setIsActive(currentData?.is_active ?? true);
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                en: nameEn || nameAr,
                ar: nameAr || nameEn,
            },
            is_active: isActive,
        };

        const onDone = { onSuccess: () => onSubmitSuccess && onSubmitSuccess() };

        if (isEdit && currentData?.id) {
            updateTaxZoneMutate({ id: currentData.id, data: payload }, onDone);
        } else {
            createTaxZoneMutate(payload, onDone);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    {isEdit
                        ? (isArabic ? "تعديل منطقة ضريبية" : "Edit Tax Zone")
                        : (isArabic ? "إضافة منطقة ضريبية" : "Add Tax Zone")}
                </Typography>
                <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
            </Box>

            <Divider />

            <Grid container spacing={3}>
                {activeLang === "ar" && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="الاسم (عربي)"
                            placeholder="مثال: منطقة الضريبة الأساسية"
                            type="text"
                            value={nameAr}
                            onChange={(e) => setNameAr(e.target.value)}
                            required
                        />
                    </Grid>
                )}
                {activeLang === "en" && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="Name (English)"
                            placeholder="e.g: Standard Tax Zone"
                            type="text"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                            required
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <InputLabel sx={{ fontWeight: "bold", mb: 1 }}>
                        {isArabic ? "تفعيل المنطقة" : "Active"}
                    </InputLabel>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        borderRadius: "12px",
                    }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                        </Typography>
                        <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
                    </Box>
                </Grid>
            </Grid>

            <Divider />

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => onSubmitSuccess && onSubmitSuccess()}
                    disabled={loadingSubmit}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "transparent",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px",
                        px: 4, py: 1,
                        fontWeight: "bold",
                        color: "text.primary",
                    }}
                >
                    {isArabic ? "إلغاء" : "Cancel"}
                </Box>
                <Box
                    component="button"
                    type="submit"
                    disabled={loadingSubmit}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        border: "none",
                        borderRadius: "12px",
                        px: 4, py: 1,
                        fontWeight: "bold",
                        minWidth: 160,
                        boxShadow: theme.shadows[2],
                    }}
                >
                    {loadingSubmit
                        ? (isArabic ? "جاري الحفظ..." : "Saving...")
                        : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                </Box>
            </Box>
        </Box>
    );
};

export default FormAddEditTaxZone;
